import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTheme } from '@/app/contexts/ThemeContext';
import { useAuth, UserProfile } from '@/app/contexts/AuthContext';
import { hasPermission } from '@/app/utils/permissions';
import styles from './Sidebar.module.scss';

interface NavItem {
  label: string;
  path: string;
  icon?: string;
  badge?: string;
  requiredPermission?: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * Navigation sections with permission requirements.
 * Items with requiredPermission will only be shown if the user has that permission.
 */
const navigationSections: NavSection[] = [
  {
    title: 'Overview',
    items: [{ label: 'Dashboard', path: '/', icon: '📊', requiredPermission: 'dashboard:read' }],
  },
  {
    title: 'Management',
    items: [
      { label: 'Users', path: '/users', icon: '👥', requiredPermission: 'users:read' },
      {
        label: 'Transactions',
        path: '/transactions',
        icon: '💳',
        requiredPermission: 'transactions:read',
      },
      {
        label: 'Partner Configurations',
        path: '/partners',
        icon: '🤝',
        requiredPermission: 'partners:read',
      },
      {
        label: 'Rewards Catalog',
        path: '/rewards',
        icon: '🎁',
        requiredPermission: 'rewards:read',
      },
      { label: 'Offers', path: '/offers', icon: '🎯', requiredPermission: 'offers:read' },
      {
        label: 'Fulfillments',
        path: '/fulfillments',
        icon: '📦',
        requiredPermission: 'fulfillments:read',
      },
      {
        label: 'Paper Journal',
        path: '/paper-journal',
        icon: '📓',
        requiredPermission: 'paper_journal:read',
      },
      {
        label: 'Marketing Banners',
        path: '/marketing-banners',
        icon: '🖼️',
        requiredPermission: 'marketing_banner:read',
      },
    ],
  },
  {
    title: 'Integrations',
    items: [
      { label: 'Jobs', path: '/jobs', icon: '⏱️', requiredPermission: 'jobs:read' },
      {
        label: 'Benepass Transfers',
        path: '/benepass-transfers',
        icon: '🔄',
        requiredPermission: 'integrations:read',
      },
      {
        label: 'InComm Cards',
        path: '/incomm-cards',
        icon: '💳',
        requiredPermission: 'integrations:read',
      },
    ],
  },
  {
    title: 'Security',
    items: [
      {
        label: 'User Management',
        path: '/security/users',
        icon: '🔐',
        requiredPermission: 'security:read',
      },
      {
        label: 'Roles & Permissions',
        path: '/security/roles',
        icon: '🛡️',
        requiredPermission: 'security:read',
      },
    ],
  },
];

/**
 * Analytics section is not implemented yet.
 * 
 * {
  title: 'Insights',
  items: [
    { label: 'Analytics', path: '/analytics', icon: '📈', requiredPermission: 'analytics:read' },
  ],
}
 */

/**
 * Filter navigation sections based on user permissions.
 * Removes items the user doesn't have permission to see.
 * Removes entire sections if no items remain after filtering.
 */
const getFilteredSections = (userProfile: UserProfile | null): NavSection[] => {
  return navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        // If no permission required, show the item
        if (!item.requiredPermission) return true;
        // Otherwise, check if user has the required permission
        return hasPermission(userProfile, item.requiredPermission);
      }),
    }))
    .filter((section) => section.items.length > 0); // Remove empty sections
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { theme, toggleTheme } = useTheme();
  const { userProfile } = useAuth();

  // Get navigation sections filtered by user permissions
  const filteredSections = getFilteredSections(userProfile);

  const handleNavClick = () => {
    // Close sidebar when clicking nav link on mobile
    if (window.innerWidth <= 768 && onClose) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && <div className={styles.sidebarOverlay} onClick={onClose} aria-hidden="true" />}
      <div className={`${styles.sidebar} ${isOpen ? styles.open : ''}`} aria-hidden={!isOpen}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <span>🎁</span>
            <span>Lore Rewards</span>
          </div>
          <div className={styles.subtitle}>Admin Dashboard</div>
        </div>

        <nav className={styles.sidebarNav}>
          {filteredSections.map((section) => (
            <div key={section.title} className={styles.navSection}>
              <div className={styles.sectionTitle}>{section.title}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `${styles.navItem} ${isActive ? styles.active : ''}`}
                  onClick={handleNavClick}
                >
                  {item.icon && <span className={styles.navIcon}>{item.icon}</span>}
                  <span className={styles.navLabel}>{item.label}</span>
                  {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.themeToggle}>
            <span className={`${styles.themeLabel} ${theme === 'dark' ? styles.active : ''}`}>
              Dark mode
            </span>
            <button
              className={styles.toggleSwitch}
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              <span className={`${styles.toggleThumb} ${theme === 'light' ? styles.light : ''}`} />
            </button>
            <span className={`${styles.themeLabel} ${theme === 'light' ? styles.active : ''}`}>
              Light mode
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
